import { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { EventsState } from '../../store/types';
import { getVariablesAndOverrides } from '../admin/document-head';

interface Props {
	eventBundle: EventsState['LiveEventReducer']['eventBundle'];
	favIcon?: string;
}
export default function LiveEventDocumentHead({ eventBundle, favIcon }: Props): JSX.Element {
	/**
	 *
	 *
	 * What's with this hot mess you ask?
	 * Well, I'll tell you.
	 *
	 * Google fonts using the @import rule send in a stylesheet, not a .ttf file or something
	 * Custom uploaded fonts use the @font-face rule
	 *
	 * We want to use Google fonts for the licensing benefits, but we also want to let them upload their own fonts
	 * So we have to use a two-hander here. If it's a google font, make an import statement. Otherwise,
	 * make a font-face rule. Easy peasy.
	 *
	 */

	useEffect(() => {
		if (favIcon) {
			// remove all existing icons so that helmet can add new ones
			const rels = document.querySelectorAll('[rel="icon"]');
			for (let i = 0; i < rels.length; i++) {
				const rel = rels?.[i];
				if (rel) {
					rel.remove();
				}
			}
		}
	}, [favIcon]);

	const { colorVariables, classOverrides } = getVariablesAndOverrides(eventBundle);

	const icons = useCallback(() => {
		if (!favIcon) {
			return [];
		}
		const n = favIcon.lastIndexOf('.');
		const extension = favIcon.substring(n + 1);
		return [
			{
				"rel": "icon",
				"type": `image/${extension}`,
				"href": favIcon
			},
			{
				"rel": "icon",
				"type": `image/${extension}`,
				"sizes": "32x32",
				"href": favIcon
			},
			{
				"rel": "icon",
				"type": `image/${extension}`,
				"sizes": "16x16",
				"href": favIcon
			}
		];
	}, [favIcon]);

	return (
		<Helmet link={[...icons()]}>
			{eventBundle?.settings?.design?.font?.heading_url ? (
				<style>
					{`
					${eventBundle?.settings.design.font.heading_url.includes(
						'fonts.googleapis.com'
					)
							? `	
							@import url(${eventBundle?.settings.design.font.heading_url});`
							: `
							@font-face {
								font-family: "${eventBundle?.settings.design.font.heading_name}";
								src: url(${eventBundle?.settings.design.font.heading_url});
							}
						`
						}
						
					${eventBundle?.settings.design.font.body_url.includes(
							'fonts.googleapis.com'
						)
							? `
							@import url(${eventBundle?.settings.design.font.body_url});`
							: `
							@font-face {
								font-family: "${eventBundle?.settings.design.font.body_name}";
								src: url(${eventBundle?.settings.design.font.body_url});
							}
						`
						}
					
					.live-event-container {
						--headingFont: "${eventBundle?.settings.design.font.heading_name}", sans-serif;
						--bodyFont: "${eventBundle?.settings.design.font.body_name}", sans-serif;
						${colorVariables}
					}
					${classOverrides}
				`}
				</style>
			) : (
				<style>
					{`@import url('https://fonts.googleapis.com/css?family=Open+Sans:300,300i,400,400i,600,600i,700,700i,800,800i');
						@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap');
					
						.editor {
							--headingFont: 'Roboto', sans-serif;
							--bodyFont: 'Open Sans', sans-serif; 
						}
					`}
				</style>
			)}

			{eventBundle?.custom_css && (
				<style>
					{eventBundle.custom_css}
				</style>
			)}
		</Helmet>
	);
}
